// @ts-strict-ignore
import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {Translatable} from 'common/i18n';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Section} from 'common/components/items-menu/types';
import {getDateRangeComparisonOptions, getPresetMatch} from 'common/report/util';
import {
  LiftType,
  LiftTypeDescriptionKey,
  LiftTypeTrackingName,
  SegmentLiftBaselineOption,
  TIME_COMPARE_NON_LIFT_KEY,
} from 'common/report/util/lift/types';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DateRangeFactory} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  getComparisonType,
  getRelativeDateFromComparisonDate,
  getTimeComparisonRangeText,
} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {emitTrackEvent} from 'common/util/dom';
import {propertiesUtils} from 'common/report/util/properties';
import {ResourceType} from 'common/report/constants';
import {SelectEvent, ToggleOption} from 'common/components/toggle/types';
import {Placement} from 'common/components/tooltip/types';
import {COMPARE_MENU_EVENTS, COMPARE_MENU_PROPNAMES} from 'common/widgets/lift-comparison-menu/tracking';
import {
  InlineCohortSegment,
  isCohortSegment,
  isSavedCohortSegment,
  SavedCohortSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {BaseDate, DateRangeType} from 'common/types/reports/bookmark';

import {template} from './template';
import {
  LiftComparison,
  LiftComparisonAttrs,
  LiftComparisonJsxProps,
  LiftComparisonParams,
  LiftComparisonState,
  LiftSelectionEvent,
  MenuDropdownState,
} from './types';
import css from './index.inline.styl';

interface MenuTooltip {
  isDisabled?: boolean;
  tooltip: {text: Translatable; placement?: Placement.Left | Placement.Right};
}

export class LiftComparisonMenu extends Component<
  LiftComparisonState,
  LiftComparisonAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  LiftComparisonParams
> {
  static get attrsSchema(): AttrsSchema<LiftComparisonAttrs> {
    return {
      ...super.attrsSchema,
      'max-data-history-days': {
        type: `number`,
        description: `max days time menu can query into data history`,
      },
      'selected-date': `json`,
      'disabled-message': {type: `string`, default: null},
      'date-range': {type: `json`, description: `interface: DateRange`},
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      breakdowns: `json`,
      'selected-breakdown-segments': `json`,
      'selected-lift-comparison': `json`,
      'time-comparison-disabled-reason': `string`,
    };
  }

  get config(): ConfigOptions<LiftComparisonState, unknown, IronContextRegistry, LiftComparisonParams> {
    return {
      ...super.config,
      template,
      css,
      useShadowDom: true,
      contexts: [`i18n` as const],
      params: {
        liftTypeOverrides: Object,
      },
      defaultParams: {
        liftTypeOverrides: {},
      },
      defaultState: {
        isMenuOpen: false,
        stagedComparisonDate: null,
        stagedComparisonType: DateRangeType.StartsOn,
        dropdownState: MenuDropdownState.List,
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  get print() {
    return this.getContext(`i18n`).print;
  }

  handleRemove(ev) {
    ev.stopPropagation();
    if (this.attr(`selected-lift-comparison`) || this.attr(`date-range`)) {
      this.trackSelection(
        this.getCompareMenuTrackingProps({
          eventName: COMPARE_MENU_EVENTS.COMPARE_CLEAR,
        }),
      );
    }
    this.dispatchEvent(new CustomEvent(`remove`));
  }

  onPaneBackClick() {
    this.update({dropdownState: MenuDropdownState.List});
  }

  getTimeComparisonOptions() {
    const maxDataHistoryDays = this.attr(`max-data-history-days`);
    const matchedPreset = getPresetMatch(this.attr(`selected-date`));
    return getDateRangeComparisonOptions(maxDataHistoryDays, matchedPreset);
  }

  onPresetMenuClick(ev) {
    const {value} = ev.detail.item;

    switch (value) {
      case `custom`:
        this.update({dropdownState: MenuDropdownState.CustomTime});
        break;
      case LiftType.Segment:
        this.update({dropdownState: MenuDropdownState.Baseline});
        break;
      case LiftType.Overall:
        this.update({isMenuOpen: false});
        this.trackSelection(
          this.getCompareMenuTrackingProps({
            newType: LiftType.Overall,
            eventName: COMPARE_MENU_EVENTS.COMPARE_SELECTION,
          }),
        );
        this.dispatchEvent(
          new CustomEvent<LiftSelectionEvent>(`select`, {
            detail: {
              // disable the date range comparison when selecting a new compare type
              comparisonDateRange: null,
              liftComparison: {
                type: LiftType.Overall,
              },
            },
          }),
        );
        break;
      default:
        // otherwise it's a comparison date
        this.update({isMenuOpen: false});
        this.onComparisonDateRangeSelected({offset: value});
        break;
    }
  }

  getDateRangePickerOptions() {
    const type = this.comparisonType;
    const dateRange = DateRangeFactory.createDateRange({type}, {displayContext: DisplayContext.RelativeRange});
    if (this.state.stagedComparisonDate) {
      dateRange.from = this.state.stagedComparisonDate.from ?? this.state.stagedComparisonDate.to;
    } else if (this.attr(`selected-date`)) {
      const {to, from} = this.attr(`selected-date`);
      if (from) {
        dateRange.from = from;
      } else if (to) {
        dateRange.from = to;
      }
    }

    if (dateRange.from && DateRange.isRelativeDate(dateRange.from)) {
      dateRange.rolling = true;
    }
    return {
      'display-context': DisplayContext.RelativeRange,
      'date-range': JSON.stringify(dateRange),
      'max-date': this.getMaxCalendarDate(),
      'max-history-days': this.attr(`max-data-history-days`),
    };
  }
  changedDateRange(ev) {
    const {from: date, type: stagedComparisonType} = ev.detail.dateRange;
    let stagedComparisonDate = this.state.stagedComparisonDate;
    if (date) {
      stagedComparisonDate = stagedComparisonType === DateRangeType.StartsOn ? {from: date} : {to: date};
    }
    this.update({
      stagedComparisonDate,
      stagedComparisonType,
    });
  }
  applyDateRange() {
    if (this.state.stagedComparisonDate) {
      this.onComparisonDateRangeSelected(this.state.stagedComparisonDate);
    }
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected-date`) {
      this.update({
        stagedComparisonDate: this.attr(`selected-date`),
        stagedComparisonType: getComparisonType(this.attr(`selected-date`)),
      });
    }
  }

  onComparisonDateRangeSelected(comparisonDate: ComparisonDate) {
    this.trackSelection(
      this.getCompareMenuTrackingProps({
        eventName: COMPARE_MENU_EVENTS.COMPARE_SELECTION,
        newType: TIME_COMPARE_NON_LIFT_KEY,
        ...this.getTrackingProps(comparisonDate),
      }),
    );
    this.dispatchEvent(
      new CustomEvent<LiftSelectionEvent>(`select`, {
        detail: {
          comparisonDateRange: comparisonDate,
          // if we're viewing a time comparison already, keep it. Otherwise, remove it so we don't have inconsistent state
          ...(this.attr(`selected-lift-comparison`)?.type === LiftType.Time ? {} : {liftComparison: null}),
        },
      }),
    );
    this.update({isMenuOpen: false, dropdownState: MenuDropdownState.List});
  }

  selectedLabel(): string {
    if (this.attr(`selected-date`)) {
      return this.labelFromDate(this.attr(`selected-date`));
    } else if (this.attr(`selected-lift-comparison`)) {
      switch (this.attr(`selected-lift-comparison`).type) {
        case LiftType.Time:
          return this.labelFromDate(this.attr(`selected-date`));
        case LiftType.Overall:
          return this.t(`reports.lift-comparison.compare-overall`);
        case LiftType.Segment:
          if (this.attr(`selected-lift-comparison`).groupValue) {
            return this.t(`reports.lift-comparison.compare-segment`, {
              segment: this.attr(`selected-lift-comparison`).groupValue,
            });
          }
          return this.t(`reports.lift-comparison.compare-segment`, {
            segment: this.attr(`selected-lift-comparison`).cohort.name,
          });
      }
    } else {
      return this.t(`reports.lift-comparison.compare`);
    }
  }

  labelFromDate(selectedDate): string {
    const {print} = this.getContext(`i18n`);
    return this.t(`reports.lift-comparison.compare-period`, {
      period: print(getTimeComparisonRangeText(selectedDate)).toLocaleLowerCase(),
    });
  }

  getMaxCalendarDate(): BaseDate {
    const dates = this.dateRange.getCalendarDatesAsOf(this.attr(`project-as-of-date`));
    let maxDate: BaseDate = dates.to;
    if (this.comparisonType === DateRangeType.StartsOn) {
      maxDate = dates.from;
    }

    return maxDate;
  }

  get dateRange(): DateRange {
    return new DateRange(this.attr(`date-range`));
  }

  get comparisonType(): DateRangeType {
    if (this.state.stagedComparisonType) {
      return this.state.stagedComparisonType;
    }
    return getComparisonType(this.attr(`selected-date`));
  }

  getTrackingProps(comparisonDate: ComparisonDate) {
    const presetMatch = getPresetMatch(comparisonDate);
    const type = presetMatch ? presetMatch.value.type : getComparisonType(comparisonDate);
    const custom = !presetMatch;
    const customRolling =
      DateRange.isRelativeDate(comparisonDate.from) || DateRange.isRelativeDate(comparisonDate.to) || false;
    const relativeDate = getRelativeDateFromComparisonDate(comparisonDate);
    return {
      eventName: `Select comparison date range`,
      props: {
        type,
        'window unit': relativeDate.unit,
        'window value': relativeDate.value,
        custom,
        'custom rolling': customRolling,
      },
    };
  }

  /**
   * PRE-REQ - the `selected-lift-comparison` has not yet been updated from the old type. This means this method _MUST_
   * be called before sending updates to change the type, otherwise newType and oldType will be the same.
   */
  getCompareMenuTrackingProps({
    eventName,
    newType,
    ...props
  }: {
    eventName: string;
    newType?: LiftType | typeof TIME_COMPARE_NON_LIFT_KEY;
  }) {
    let oldType = null;
    if (this.attr(`selected-lift-comparison`)?.type) {
      oldType = this.attr(`selected-lift-comparison`)?.type;
    } else if (this.attr(`date-range`)) {
      oldType = TIME_COMPARE_NON_LIFT_KEY;
    }

    return {
      eventName,
      props: {
        ...(newType ? {[COMPARE_MENU_PROPNAMES.COMPARE_TYPE]: LiftTypeTrackingName[newType]} : {}),
        ...(oldType ? {[COMPARE_MENU_PROPNAMES.PREVIOUS_STATE]: LiftTypeTrackingName[oldType]} : {}),
        ...props,
      },
    };
  }

  public trackSelection(props, cancelable = true) {
    emitTrackEvent(this, {...props, eventPrefix: null, cancelable});
  }

  getMenuTooltipsAndDisabledState(): {time: MenuTooltip; baseline: MenuTooltip; overall: MenuTooltip} {
    let baseline: MenuTooltip;
    let time: MenuTooltip;
    let overall: MenuTooltip;

    const liftTypeOverrides = this.params.liftTypeOverrides;

    if (this.attr(`time-comparison-disabled-reason`)) {
      time = {
        isDisabled: true,
        tooltip: {text: this.attr(`time-comparison-disabled-reason`)},
      };
    } else {
      time = {
        tooltip: {
          text: this.t(`reports.lift-comparison.tooltips.period`),
          placement: Placement.Left,
        },
      };
    }

    if (liftTypeOverrides[LiftType.Segment] && !liftTypeOverrides[LiftType.Segment].enabled) {
      baseline = {
        isDisabled: true,
        tooltip: {text: liftTypeOverrides[LiftType.Segment].reason},
      };
    } else if (this.attr(`breakdowns`).length === 0) {
      baseline = {
        isDisabled: true,
        tooltip: {
          text: this.t(`reports.lift-comparison.tooltips.disabled`, {
            liftType: this.t(LiftTypeDescriptionKey[LiftType.Segment]),
          }),
        },
      };
    } else {
      baseline = {
        tooltip: {text: this.t(`reports.lift-comparison.tooltips.segmented`)},
      };
    }

    if (liftTypeOverrides[LiftType.Overall]) {
      const enabled = liftTypeOverrides[LiftType.Overall].enabled;
      const tooltipText = enabled
        ? this.t(`reports.lift-comparison.tooltips.unsegmented`)
        : liftTypeOverrides[LiftType.Overall].reason;
      overall = {
        isDisabled: !enabled,
        tooltip: {text: tooltipText},
      };
    } else if (this.attr(`selected-breakdown-segments`).length === 0) {
      overall = {
        isDisabled: true,
        tooltip: {
          text: this.t(`reports.lift-comparison.tooltips.disabled`, {
            liftType: this.t(LiftTypeDescriptionKey[LiftType.Overall]),
          }),
        },
      };
    } else {
      overall = {
        tooltip: {text: this.t(`reports.lift-comparison.tooltips.unsegmented`)},
      };
    }

    return {
      baseline,
      time,
      overall,
    };
  }

  getSections(): Section[] {
    const {time, baseline, overall} = this.getMenuTooltipsAndDisabledState();

    return [
      {
        items: [
          {
            label: this.t(`reports.lift-comparison.period`),
            icon: `type-date`,
            subSections: getDateRangeComparisonOptions(
              this.attr(`max-data-history-days`),
              getPresetMatch(this.attr(`selected-date`)),
            ),
            ...time,
          },
          {
            label: this.t(`reports.lift-comparison.segment`),
            icon: `segment`,
            value: LiftType.Segment,
            ...baseline,
          },
          {
            label: this.t(`reports.lift-comparison.overall`),
            icon: `value-relative`,
            value: LiftType.Overall,
            ...overall,
          },
        ],
      },
    ];
  }

  getSegmentNameAndLabel(): {segment: string; segmentLabel: string; comparisonProperty?: any} {
    const allBreakdowns = this.attr(`breakdowns`);
    let lastBreakdown = allBreakdowns[allBreakdowns.length - 1];
    if (Array.isArray(lastBreakdown)) {
      lastBreakdown = lastBreakdown[0] as Segment;
    }

    const segmentation = Object.values(lastBreakdown)[0];

    let segmentKey = segmentation.propertyName;
    if (segmentation.propertyObjectKey) {
      segmentKey += `.` + segmentation.propertyObjectKey.join(`.`);
    }

    const segmentLabel = propertiesUtils.renameProperty(
      segmentation.propertyName,
      segmentation.resourceType,
      this.getContext(`i18n`),
    );

    return {
      segment: segmentKey,
      segmentLabel,
      comparisonProperty: segmentation,
    };
  }

  applyBaseline(ev: CustomEvent<{value: SegmentLiftBaselineOption}>) {
    const {value} = ev.detail;
    let liftComparison: LiftComparison;

    this.trackSelection(
      this.getCompareMenuTrackingProps({
        eventName: COMPARE_MENU_EVENTS.COMPARE_SELECTION,
        newType: LiftType.Segment,
      }),
    );

    const lastBreakdown: Segment = this.attr(`breakdowns`)?.[this.attr(`breakdowns`).length - 1];

    if (isCohortSegment(lastBreakdown)) {
      const matchingOption = this.attr(`selected-breakdown-segments`).filter((option) => {
        const cohortSegment = option.cohortSegment;
        const negatedMatches =
          cohortSegment[ResourceType.Cohort].negated ===
          (value.cohortSegment as SavedCohortSegment)[ResourceType.Cohort].negated;
        if (isSavedCohortSegment(cohortSegment)) {
          return (
            negatedMatches &&
            cohortSegment[ResourceType.Cohort].id ===
              (value.cohortSegment as SavedCohortSegment)[ResourceType.Cohort].id
          );
        }
        return (
          negatedMatches &&
          cohortSegment[ResourceType.Cohort].raw_cohort.name ===
            (value.cohortSegment as InlineCohortSegment)[ResourceType.Cohort].name
        );
      })[0];
      // including the segment name so that the baseline value can be consistently found in the response
      // The displayName might be this, but build it ourselves to be sure
      // TODO: reliably and consistently set this in Query-API
      const cohortDisplayName =
        (matchingOption.cohortSegment[ResourceType.Cohort].negated ? `Not In ` : ``) +
        matchingOption.cohortSegment[ResourceType.Cohort].name;
      liftComparison = {
        type: LiftType.Segment,
        groupValue: cohortDisplayName,
        cohort: matchingOption.cohortSegment[ResourceType.Cohort],
      };
    } else {
      const segmentNameAndLabel = this.getSegmentNameAndLabel();
      liftComparison = {
        type: LiftType.Segment,
        groupValue: value.propertyValue,
        comparisonProperty: segmentNameAndLabel.comparisonProperty,
      };
    }
    this.dispatchEvent(
      new CustomEvent<LiftSelectionEvent>(`select`, {
        detail: {
          // disable the date range comparison when selecting a new compare type
          comparisonDateRange: null,
          liftComparison,
        },
      }),
    );

    this.update({
      isMenuOpen: false,
      dropdownState: MenuDropdownState.List,
    });
  }

  liftToggleOptions(): ToggleOption[] {
    return [
      {
        icon: `value-absolute`,
        value: `absolute`,
        tooltipLabel: this.t(`reports.lift-comparison.tooltips.lift-absolute`),
        label: ``,
      },
      {
        icon: `lift`,
        value: `lift`,
        tooltipLabel: this.t(`reports.lift-comparison.tooltips.lift-percent`),
        label: ``,
      },
    ];
  }

  handleLiftToggle(ev: SelectEvent) {
    const liftComparison: LiftComparison =
      ev.detail.selected === `lift`
        ? {
            type: LiftType.Time,
          }
        : null;

    this.trackSelection(
      this.getCompareMenuTrackingProps({
        eventName: liftComparison ? COMPARE_MENU_EVENTS.COMPARE_SELECTION : COMPARE_MENU_EVENTS.COMPARE_CLEAR,
        newType: liftComparison ? LiftType.Time : TIME_COMPARE_NON_LIFT_KEY,
      }),
    );

    this.dispatchEvent(new CustomEvent<LiftSelectionEvent>(`select`, {detail: {liftComparison}}));
  }

  isLiftToggleAvailable() {
    return Boolean(this.attr(`selected-date`)) && this.params.liftTypeOverrides[LiftType.Time]?.enabled !== false;
  }
}

export const LiftComparisonEl = defineAndMakeJsxFactory<LiftComparisonJsxProps>(
  `mp-lift-comparison-menu`,
  LiftComparisonMenu,
);
export const LiftComparisonReact = panel2react<LiftComparisonJsxProps>(`mp-lift-comparison-menu`, LiftComparisonMenu);
